import { useEffect, useState } from "react";
import { Loader2, RotateCcw, Sparkles } from "lucide-react";
import { apiFetch, ApiError } from "@/lib/api-client";
import { useStylePanel } from "../style-panel-context";

interface StylePromptResponse {
  rationale: string;
  styleData: Record<string, unknown>;
}

interface UndoState {
  componentId: string;
  styleData: Record<string, unknown> | undefined;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function cloneStyleData<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function formatStylePromptError(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 429) {
      return "AI style generation is rate limited right now. Try again in a moment.";
    }

    if (error.status >= 500) {
      return "AI style generation is temporarily unavailable.";
    }

    return error.message;
  }

  return error instanceof Error ? error.message : "AI style generation failed.";
}

export function StylePromptGenerator() {
  const {
    selectedComponentId,
    selectedComponentType,
    getSelectedComponentProps,
    replaceSelectedDssStyles,
  } = useStylePanel();
  const [prompt, setPrompt] = useState("");
  const [rationale, setRationale] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [undoState, setUndoState] = useState<UndoState | null>(null);

  useEffect(() => {
    setError(null);
    setRationale(null);
  }, [selectedComponentId]);

  const handleGenerate = async () => {
    if (!selectedComponentId || !selectedComponentType) {
      setError("Select a block before generating AI styles.");
      return;
    }

    const trimmedPrompt = prompt.trim();
    if (trimmedPrompt.length < 3) {
      setError("Describe the visual direction in at least 3 characters.");
      return;
    }

    const currentProps = getSelectedComponentProps();
    const currentStyleData = isRecord(currentProps?._dssStyles)
      ? cloneStyleData(currentProps._dssStyles)
      : undefined;

    setIsGenerating(true);
    setError(null);

    try {
      const response = await apiFetch<{ data: StylePromptResponse }>("/crm/pages/generate/style-from-prompt", {
        method: "POST",
        body: JSON.stringify({
          prompt: trimmedPrompt,
          componentType: selectedComponentType,
          currentStyleData,
        }),
      });

      setUndoState({
        componentId: selectedComponentId,
        styleData: currentStyleData,
      });
      replaceSelectedDssStyles(cloneStyleData(response.data.styleData));
      setRationale(response.data.rationale);
    } catch (requestError) {
      setError(formatStylePromptError(requestError));
    } finally {
      setIsGenerating(false);
    }
  };

  const handleUndo = () => {
    if (!selectedComponentId || !undoState || undoState.componentId !== selectedComponentId) {
      return;
    }

    replaceSelectedDssStyles(undoState.styleData ? cloneStyleData(undoState.styleData) : undefined);
    setRationale(null);
    setUndoState(null);
  };

  const canUndo = Boolean(selectedComponentId && undoState && undoState.componentId === selectedComponentId);

  return (
    <div className="space-y-3 border-b border-border bg-muted/20 p-3">
      <div>
        <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          AI Style Prompt
        </div>
        <p className="mt-1 text-xs leading-5 text-muted-foreground">
          Describe the look you want. The generated styles apply only to the selected block and can be undone.
        </p>
      </div>

      <div className="rounded-xl border border-border bg-background p-3">
        <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
          Selected block
        </div>
        <div className="mt-1 text-sm font-medium text-foreground">
          {selectedComponentType ?? "No block selected"}
        </div>
      </div>

      <label className="block">
        <span className="sr-only">Style prompt</span>
        <textarea
          value={prompt}
          onChange={(event) => setPrompt(event.target.value)}
          placeholder="Make it luxurious, editorial, and high trust."
          className="min-h-24 w-full rounded-xl border border-border bg-background px-3 py-2 text-sm text-foreground outline-none ring-0 placeholder:text-muted-foreground/80 focus:border-[var(--color-primary)]"
        />
      </label>

      {error ? (
        <div className="rounded-xl border border-destructive/25 bg-destructive/5 px-3 py-2 text-xs text-destructive">
          {error}
        </div>
      ) : null}

      {rationale ? (
        <div className="rounded-xl border border-border bg-background px-3 py-2 text-xs leading-5 text-muted-foreground">
          <span className="font-medium text-foreground">Why this direction:</span> {rationale}
        </div>
      ) : null}

      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => void handleGenerate()}
          disabled={isGenerating || !selectedComponentId}
          className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-primary px-3 py-2 text-sm font-medium text-primary-foreground transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isGenerating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
          {isGenerating ? "Applying..." : "Generate and Apply"}
        </button>
        <button
          type="button"
          onClick={handleUndo}
          disabled={!canUndo}
          className="inline-flex items-center justify-center gap-2 rounded-xl border border-border bg-background px-3 py-2 text-sm font-medium text-foreground transition hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50"
        >
          <RotateCcw className="h-4 w-4" />
          Undo
        </button>
      </div>
    </div>
  );
}
